import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { FOUNDER_PATHS, FOUNDER_OG_IMAGE, founder } from "../i18n/founder";
import { founderJsonLd } from "../lib/founderSchema";
import { BRAND } from "../config/brand";
import { Seo } from "../components/Seo";
import {
  FounderPortrait, JourneySection, ApproachSection, OutreachSection, TrainingSection, SynriseSection, VisionSection, SocialsSection,
} from "../components/founder/FounderSections";

const ALTERNATES = [
  ...Object.entries(FOUNDER_PATHS).map(([hreflang, path]) => ({ hreflang, path })),
  { hreflang: "x-default", path: FOUNDER_PATHS.it },
];

const LangLinks = ({ lang }) => (
  <nav aria-label="Language" className="flex items-center rounded-full border border-white/10 bg-white/[0.04] p-1 backdrop-blur-xl" data-testid="founder-lang-links">
    {Object.entries(FOUNDER_PATHS).map(([code, path]) => (
      <Link key={code} to={path} hrefLang={code} data-testid={`founder-lang-${code}`} aria-current={lang === code ? "page" : undefined}
        className={`flex h-8 w-10 items-center justify-center rounded-full font-mono text-[12px] font-medium tracking-wider transition-colors ${lang === code ? "bg-gold text-ink" : "text-zinc-400 hover:text-zinc-100"}`}>
        {code.toUpperCase()}
      </Link>
    ))}
  </nav>
);

const Hero = ({ c }) => (
  <section className="relative overflow-hidden px-5 pb-20 pt-14 sm:px-8 sm:pt-20" data-testid="founder-hero">
    <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5 lg:order-2"><FounderPortrait alt={c.photoAlt} /></div>
      <div className="lg:col-span-7 lg:order-1">
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold" data-testid="founder-eyebrow">{c.eyebrow}</p>
        <h1 className="mt-5 break-words font-display text-[2rem] font-extrabold leading-[1.05] tracking-[-0.03em] text-zinc-50 sm:text-5xl lg:text-6xl" data-testid="founder-h1">
          Abdelouahed El Allali
        </h1>
        <p className="mt-3 font-serif text-2xl italic text-zinc-300 sm:text-3xl">{c.aka}</p>
        <ul className="mt-7 flex flex-wrap gap-2" data-testid="founder-roles">
          {c.roles.map((r, i) => (
            <li key={r} className={`rounded-full border px-3.5 py-1.5 text-[13px] ${i === 0 ? "border-gold bg-gold font-semibold text-ink" : "border-gold/25 bg-gold/[0.07] text-zinc-100"}`}>{r}</li>
          ))}
        </ul>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-300 sm:text-lg" data-testid="founder-intro">{c.intro}</p>
      </div>
    </div>
  </section>
);

export default function Founder({ lang }) {
  const { setLang } = useLang();
  const c = founder[lang];

  useEffect(() => {
    setLang(lang);
    window.scrollTo(0, 0);
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-[100svh]" data-testid="founder-page">
      <Seo title={c.seoTitle} description={c.desc} path={FOUNDER_PATHS[lang]} lang={lang} alternates={ALTERNATES} jsonLd={founderJsonLd(lang)}
        image={{ ...FOUNDER_OG_IMAGE, alt: c.photoAlt }} />

      <header className="sticky top-0 z-50 border-b border-white/5 bg-ink/80 backdrop-blur-xl" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-5 sm:px-8">
          <Link to="/" data-testid="founder-back-link" className="flex items-center gap-2 text-sm text-zinc-300 transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" />
            <img src={BRAND.logoSmall} alt="SYNrise" className="h-7 w-7" />
            <span className="hidden sm:inline">{c.back}</span>
          </Link>
          <LangLinks lang={lang} />
        </div>
      </header>

      <main>
        <Hero c={c} />
        <div className="mx-auto max-w-6xl space-y-24 px-5 pb-24 sm:px-8">
          <JourneySection c={c} />
          <ApproachSection c={c} />
          <OutreachSection c={c} />
          <TrainingSection c={c} />
          <SynriseSection c={c} />
          <VisionSection c={c} />
          <SocialsSection c={c} />
          <section className="rounded-[28px] border border-white/[0.07] bg-white/[0.02] p-6 sm:p-8" data-testid="founder-disclaimer">
            <h2 className="font-mono text-[12px] uppercase tracking-[0.3em] text-zinc-300">{c.disclaimerTitle}</h2>
            {c.disclaimer.map((p) => <p key={p.slice(0, 24)} className="mt-4 text-[13px] leading-relaxed text-zinc-400">{p}</p>)}
          </section>
        </div>
      </main>
    </div>
  );
}
